/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TokenService } from '../../../../client/app/authentication/token/token.service';
import { ClientsService } from '../../../../client/app/clients/clients.service';
import { EnvService } from '../../../../client/app/configuration/env.service';
import { LocationContextService } from '../../../../client/app/location-context/location-context.service';
import { ConnectionStatus } from '../../../../model/connection-status';
import { IWebSocketMessage, WebSocketMessageType } from '../../../../model/web-socket-message';
import { LogService } from '../../logging/log.service';
import { ServerMonitorService } from './server-monitor.service';
import { WebSocketMessageService } from './websocket-message.service';

@Injectable({
    providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
    LOG_SOURCE = 'WebSocketService';
    subLogMessages: Subscription;
    localWebSocket: WebSocket;

    constructor(
        private readonly webSocketMessageService: WebSocketMessageService,
        private readonly clientsService: ClientsService,
        private readonly locationContextService: LocationContextService,
        private readonly logService: LogService,
        private readonly tokenService: TokenService,
        private readonly serverMonitorService: ServerMonitorService,
        private readonly env: EnvService
    ) {}

    private getCorrelationId(): Promise<string> {
        return new Promise(resolve => {
            const correlationId = this.clientsService.getCorrelationId();
            if (!correlationId) {
                throw new Error('Correlation id is undefined');
            }
            resolve(correlationId);
        });
    }

    private getToken(): Promise<string> {
        return this.tokenService.getToken();
    }

    private createWebSocketMessageForSending(
        correlationId: string,
        token: string,
        messageType: WebSocketMessageType,
        data: any
    ): IWebSocketMessage {
        const message: IWebSocketMessage = {
            headers: {
                request: messageType,
                authorization: `Bearer ${token}`,
                correlationId: correlationId
            },
            payload: data
        };

        message.payload = {
            ...message.payload,
            correlationId: correlationId,
            locationContext: this.locationContextService.getLocationContext()
        };

        return message;
    }

    private createWebSocketMessageForProcessing(dataObject: any): IWebSocketMessage {
        return {
            headers: {
                response: dataObject?.messageType,
                correlationId: dataObject?.correlationId
            },
            payload: dataObject?.data
        };
    }

    private setLocalWebSocket(webSocket: WebSocket) {
        this.localWebSocket = webSocket;
    }

    private getLocalWebSocketWhenOpen(): WebSocket {
        if (!this.localWebSocket) {
            return null;
        }
        if (this.localWebSocket.readyState !== 1) {
            return null;
        }
        return this.localWebSocket;
    }

    private getOpenWebSocket(correlationId: string, token: string): Promise<WebSocket> {
        return new Promise<WebSocket>(resolve => {
            let webSocket = this.getLocalWebSocketWhenOpen();
            if (webSocket) {
                this.logService.verbose(this.LOG_SOURCE, 'Resolving WebSocket from local object', { webSocket });
                resolve(webSocket);
                return;
            }

            webSocket = new WebSocket(`${this.env.config.websocket.url}?uid=${correlationId}&token=${token}`);
            this.logService.info(this.LOG_SOURCE, 'WebSocket is CREATED!', { webSocket });

            webSocket.onclose = () => {
                this.logService.info(this.LOG_SOURCE, 'WebSocket is CLOSED!', {});
                this.serverMonitorService.updateConnectionStatus(ConnectionStatus.Offline);
            };

            webSocket.onmessage = (event: MessageEvent) => {
                const dataObject = JSON.parse(event.data);
                const wsMessage = this.createWebSocketMessageForProcessing(dataObject);
                this.logService.info(this.LOG_SOURCE, `Received message [${wsMessage.headers.response}]`, wsMessage);
                this.webSocketMessageService.processMessage(wsMessage);
            };

            webSocket.onopen = () => {
                resolve(webSocket);
            };

            this.setLocalWebSocket(webSocket);
        });
    }

    sendMessage(messageType: WebSocketMessageType, data: any) {
        this.getCorrelationId()
            .then(correlationId => {
                this.getToken()
                    .then(token => {
                        this.getOpenWebSocket(correlationId, token)
                            .then(webSocket => {
                                const message = this.createWebSocketMessageForSending(
                                    correlationId,
                                    token,
                                    messageType,
                                    data
                                );
                                this.logService.info(this.LOG_SOURCE, `Sending message [${messageType}]`, message);
                                const messageString = JSON.stringify(message);
                                webSocket.send(messageString);
                            })
                            .catch(error =>
                                this.logService.error(this.LOG_SOURCE, 'Error getting open web socket', error)
                            );
                    })
                    .catch(error => this.logService.error(this.LOG_SOURCE, 'Error getting token', error));
            })
            .catch(error => this.logService.error(this.LOG_SOURCE, 'Error getting correlation id', error));
    }

    ngOnDestroy() {
        if (this.subLogMessages) {
            this.subLogMessages.unsubscribe();
        }
    }
}
